import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './OrderRefund.module.css';

interface OrderInfo {
  id: string;
  customer_id: string;
  customer_name?: string;
  product_id: string;
  order_name?: string;
  payment_state: string;
  payment_key?: string;
  amount?: number;
  is_gift?: boolean;
  phone_number?: string;
  created_at: string;
  updated_at?: string;
}

const OrderRefund: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getOrderDetail, refundOrder } = useApi();
  
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  
  // 폼 상태
  const [formData, setFormData] = useState({
    refund_reason: '',
    admin_note: '',
    refund_amount: 0
  });

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!orderId) return;

      try {
        setLoading(true);
        setError(null);

        const orderData = await getOrderDetail(orderId);
        setOrder(orderData);
        
        // 환불 금액 초기화 (전체 금액)
        setFormData(prev => ({
          ...prev,
          refund_amount: orderData.amount || 0
        }));
      } catch (err: any) {
        setError(err.message || '주문 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderId, getOrderDetail]);

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 금액 포맷팅
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  // 환불 사유 옵션
  const refundReasonOptions = [
    { value: 'customer_request', label: '고객 요청', icon: '👤' },
    { value: 'payment_error', label: '결제 오류', icon: '💳' },
    { value: 'service_issue', label: '서비스 문제', icon: '🔧' },
    { value: 'duplicate_payment', label: '중복 결제', icon: '🔄' },
    { value: 'system_error', label: '시스템 오류', icon: '💻' },
    { value: 'other', label: '기타', icon: '📝' }
  ];

  // 폼 입력 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'refund_amount' ? Number(value) : value
    }));
  };

  // 환불 처리
  const handleRefund = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!order) return;

    // 환불 사유 확인
    if (!formData.refund_reason) {
      alert('❌ 환불 사유를 선택해주세요.');
      return;
    }

    // 환불 금액 확인
    if (formData.refund_amount <= 0) {
      alert('❌ 환불 금액은 0원보다 커야 합니다.');
      return;
    }

    if (formData.refund_amount > (order.amount || 0)) {
      alert('❌ 환불 금액은 주문 금액을 초과할 수 없습니다.');
      return;
    }

    // 최종 확인
    const confirmMessage = `환불 처리를 진행하시겠습니까?\n\n` +
      `주문 ID: ${order.id}\n` +
      `환불 금액: ${formatAmount(formData.refund_amount)}\n` +
      `환불 사유: ${refundReasonOptions.find(opt => opt.value === formData.refund_reason)?.label}\n\n` +
      `⚠️ 이 작업은 되돌릴 수 없습니다.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setProcessing(true);
      
      const updateData = {
        payment_state: 'REFUNDED',
        refund_reason: formData.refund_reason,
        admin_note: formData.admin_note,
        refund_amount: formData.refund_amount,
        refunded_at: new Date().toISOString()
      };

      const response = await refundOrder(order.id, updateData);
      
      // API 응답이 성공적으로 반환되면 성공으로 처리
      if (response) {
        alert('✅ 환불 처리가 성공적으로 완료되었습니다.');
        handleGoBack();
      } else {
        alert('❌ 환불 처리에 실패했습니다.');
      }
    } catch (error: any) {
      alert(`❌ 환불 처리 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // 뒤로가기 처리
  const handleGoBack = () => {
    const state = location.state;
    if (state && state.from) {
      navigate(state.from);
    } else {
      navigate(`/admin/orders/detail/${orderId}`);
    }
  };

  // 취소 처리
  const handleCancel = () => {
    if (window.confirm('환불 처리를 취소하시겠습니까?\n\n입력한 정보는 저장되지 않습니다.')) {
      handleGoBack();
    }
  };

  if (loading) {
    return (
      <div className={styles.orderRefund}>
        <div className={styles.pageTitleRow}>
          <h1>환불 처리</h1>
        </div>
        <div className={styles.loadingSpinner}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
          로딩 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.orderRefund}>
        <div className={styles.pageTitleRow}>
          <h1>환불 처리</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
          {error}
          <button onClick={handleGoBack} className={styles.btnBack}>
            ← 뒤로가기
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={styles.orderRefund}>
        <div className={styles.pageTitleRow}>
          <h1>환불 처리</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>🔍</div>
          주문 정보를 찾을 수 없습니다.
          <button onClick={handleGoBack} className={styles.btnBack}>
            ← 뒤로가기
          </button>
        </div>
      </div>
    );
  }

  // 이미 환불된 주문인지 확인
  if (order.payment_state === 'REFUNDED') {
    return (
      <div className={styles.orderRefund}>
        <div className={styles.pageTitleRow}>
          <h1>환불 처리</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚠️</div>
          이미 환불 처리된 주문입니다.
          <button onClick={handleGoBack} className={styles.btnBack}>
            ← 뒤로가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orderRefund}>
      <div className={styles.pageTitleRow}>
        <h1>환불 처리</h1>
        <button onClick={handleCancel} className={styles.btnCancel}>
          ✕ 취소
        </button>
      </div>
      
      <div className={styles.orderRefundContent}>
        {/* 주문 정보 표시 */}
        <div className={styles.orderInfoSection}>
          <h2>📋 환불 대상 주문 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>주문 ID</label>
              <span>{order.id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문자명</label>
              <span>{order.customer_name || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>상품 ID</label>
              <span>{order.product_id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문명</label>
              <span>{order.order_name || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>결제 상태</label>
              <span className={styles.paymentState}>{order.payment_state}</span>
            </div>
            <div className={styles.infoItem}>
              <label>결제 키</label>
              <span>{order.payment_key || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문 금액</label>
              <span style={{ fontWeight: '600', color: '#059669' }}>{formatAmount(order.amount)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문일</label>
              <span>{formatDate(order.created_at)}</span>
            </div>
          </div>
        </div>

        {/* 환불 처리 폼 */}
        <div className={styles.refundFormSection}>
          <h2>💸 환불 처리</h2>
          <form onSubmit={handleRefund} className={styles.refundForm}>
            <div className={styles.formGrid}>
              <div className={styles.formItem}>
                <label htmlFor="refund_reason">환불 사유 *</label>
                <select
                  id="refund_reason"
                  name="refund_reason"
                  value={formData.refund_reason}
                  onChange={handleInputChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="">환불 사유를 선택하세요</option>
                  {refundReasonOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formItem}>
                <label htmlFor="refund_amount">환불 금액 *</label>
                <div className={styles.amountInputWrapper}>
                  <input
                    type="number"
                    id="refund_amount"
                    name="refund_amount"
                    value={formData.refund_amount}
                    onChange={handleInputChange}
                    min="0"
                    max={order.amount || 0}
                    step="1000"
                    required
                    className={styles.formInput}
                  />
                  <span className={styles.amountUnit}>원</span>
                </div>
                <div className={styles.amountInfo}>
                  <span>최대 환불 가능 금액: {formatAmount(order.amount)}</span>
                </div>
              </div>

              <div className={styles.formItem}>
                <label htmlFor="admin_note">관리자 메모</label>
                <textarea
                  id="admin_note"
                  name="admin_note"
                  value={formData.admin_note}
                  onChange={handleInputChange}
                  placeholder="환불 처리에 대한 추가 메모를 입력하세요"
                  rows={4}
                  className={styles.formTextarea}
                />
              </div>
            </div>

            {/* 환불 요약 */}
            <div className={styles.refundSummary}>
              <h3>📊 환불 요약</h3>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <label>원 주문 금액</label>
                  <span>{formatAmount(order.amount)}</span>
                </div>
                <div className={styles.summaryItem}>
                  <label>환불 금액</label>
                  <span className={styles.refundAmount}>{formatAmount(formData.refund_amount)}</span>
                </div>
                <div className={styles.summaryItem}>
                  <label>환불 사유</label>
                  <span>{formData.refund_reason ? refundReasonOptions.find(opt => opt.value === formData.refund_reason)?.label : '-'}</span>
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={handleCancel}
                className={styles.btnCancel}
                disabled={processing}
              >
                ✕ 취소
              </button>
              <button
                type="submit"
                className={styles.btnRefund}
                disabled={processing}
              >
                {processing ? '💸 환불 처리 중...' : '💸 환불 처리'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderRefund;
